/**
 * The FREKWENCJA card — the summary column's, and the Trendy tab's wide one. One `/consistency`
 * read answers both.
 *
 * ── v3's figures, and the strip the owner put back ─────────────────────────────────────────────
 * KANON `Detal klienta v3` draws a hero figure over the fortnight, a row of squares across the
 * thirty-day window, and the window's own sentence — and on Trendy, the same window as a ring with
 * counters beside it. The FIGURES are v3's and stay: the hero the owner cut on 13 August 2026 came
 * back with v3, and so did the thirty-day sentence.
 *
 * The row of squares did not survive contact. Owner, 1 September 2026: he could not read it. So the
 * strip is what it was before v3 — one cell per calendar DAY, each column naming its weekday, under
 * a legend that names every state in words — and the styling is v3's. One thing from the 13.08
 * disposition still holds: the previous-window COMPARISON stays gone ("totalnie confusing"). The
 * other did not survive the same day's second look — see the next block.
 *
 * The thirty-day SENTENCE under the strip went too, later on 1 September: "a ten tekst usuńmy".
 * The card now carries one window instead of three, which is what made the three captions
 * load-bearing in the first place. `window` stays on the projection because the Trendy tab's grid
 * and its ring both read it.
 *
 * `cells` — v3's one-entry-per-SCHEDULED-session projection — is deleted with the row it fed.
 * Nothing else read it, and the imprecision it carried is the reason it is not kept:
 * `BuildConsistencyAsync` emits one entry per calendar DAY carrying a single verdict, so a client
 * who trained twice on a Tuesday contributed one "session" cell. Nothing on the contract counts
 * sessions over a window — see `AGENTS.md`, "Gaps against the contract" — so every cell this module
 * emits is a DAY, and the copy beside it says so.
 *
 * ── The strip and the hero figure share ONE window ────────────────────────────────────────────
 * Owner, 1 September 2026: "zróbmy ostatnie 14 dni, skoro wyżej jest »4 z 7 sesji w ostatnich 14
 * dniach«, a nie siedem." The card printed a fortnight as its figure and drew a week 40px under
 * it — two spans, one card, and nothing said they were different. So there is now one constant,
 * `ATTENDANCE_RECENT_DAYS`: `recent` folds it and `recentDays` draws it, and neither can name a
 * span the other does not. The seven-day slice that used to sit here is gone with the mismatch.
 *
 * The roster's preview panel still draws seven, and that is not an oversight — its own copy says
 * "ostatnie 7 dni" and it reads a different source. The span travels as the length of the array
 * handed to `shared/ui/TrainingWeekStrip`, so the two surfaces share the drawing without sharing
 * the window.
 *
 * ── `days` is the SAME window drawn as a calendar, and its copy says "dzień" ──────────────────
 * Owner, 1 September 2026: the Trendy card's strip was unreadable — squares carrying their state in
 * a fill alone, with no legend and no dates, so a trainer could see neither what a square meant nor
 * which day it was. The repair needs an axis, and an axis needs contiguous days: you cannot anchor
 * "start of the week" over a sparse list of the days that happened to be scheduled.
 *
 * So `days` carries EVERY calendar day in the window, rest days included, and the copy that goes
 * with it says day rather than session — which is also the honest word for what `/consistency`
 * emits. `recentDays` is the last fortnight of exactly those cells, so the two strips on this
 * screen cannot disagree about what a given Tuesday was.
 *
 * A fourth state, *zaplanowana*, is deliberately absent from BOTH grids, and the reason was
 * re-verified on 1 September 2026 rather than assumed. Two things make it unreachable, either of
 * which would be enough: every caller's window ends today (`attendanceRange` asks `to = today`,
 * and `trendsRange` refuses a range that ends in the future), and `/consistency` has no word for a
 * session still ahead in any case — `OccurrenceDayClassifier.Contribution` folds `Upcoming` and
 * `InProgress` into `None`, indistinguishable from a rest day. That is why the roster's seven-day
 * card reads the CALENDAR instead, and why these strips name three states rather than four: a
 * legend entry that can never appear is a question the reader cannot answer.
 *
 * ── Why `/consistency` and not `/compliance` ───────────────────────────────────────────────────
 * `/compliance` returns a 30-day figure of its own, but `/consistency`'s per-day verdicts fold to
 * the same number and the summary column already pays for that read; two endpoints answering one
 * sentence is how two numbers learn to disagree.
 *
 * ── The window is the request's business ───────────────────────────────────────────────────────
 * `buildAttendance` folds whatever days it is handed and takes no dates. Asking for exactly thirty
 * days is the caller's decision, made once in `api/`.
 */

import { mondayFirstWeekday, MS_PER_DAY, parseDateOnly } from '~/shared/lib/calendarDate';
import type { Translate } from '~/shared/lib/localeTranslate';

/** What one calendar day did, in the contract's own words. */
export type AttendanceState = 'Trained' | 'Missed' | 'None';

export interface AttendanceDay {
  /** `YYYY-MM-DD`, the contract's `DateOnly`. Compared as a string; never zone-converted. */
  readonly date: string;
  readonly state: AttendanceState;
}

export interface AttendanceWindow {
  /** Days trained in the window. A day with two sessions is one day — see the header. */
  readonly done: number;
  /** Days scheduled in the window — trained plus missed. Rest days are not counted. */
  readonly total: number;
  /** `done / total` as a whole percent, or `null` when nothing was scheduled. */
  readonly pct: number | null;
}

/**
 * One cell, one CALENDAR day. `free` is a day nothing was asked on — not a failure, and drawing it
 * as one would be a lie the grid tells silently.
 *
 * The shape is `shared/ui/TrainingWeekStrip`'s `TrainingDayMark`, which is what both strips render
 * through. It is matched structurally rather than imported: `model/` holds no import of a component.
 */
export interface AttendanceDayMark {
  readonly date: string;
  readonly state: 'done' | 'missed' | 'free';
  /** Monday-first weekday index, 0–6. Named so a cell can say which day it was. */
  readonly weekday: number;
}

/** A day on the Trendy tab's wide grid: the same mark, plus whether the grid dates it. */
export interface AttendanceDayCell extends AttendanceDayMark {
  /**
   * This cell opens a week, so the grid prints its date underneath.
   *
   * Mondays, plus the first cell whatever weekday it lands on — a strip whose first label sat five
   * squares in gives the eye nothing to start from. Every seventh label rather than thirty is the
   * whole point: the owner asked to be able to put a finger on a day without counting squares, and
   * thirty microscopic dates are as unreadable as none.
   */
  readonly anchor: boolean;
}

export interface AttendanceView {
  /** The window, one cell per calendar day, oldest first — the Trendy tab's grid. See the header. */
  readonly days: readonly AttendanceDayCell[];
  /**
   * The last `ATTENDANCE_RECENT_DAYS` calendar days of the same window, oldest first — the summary
   * column's strip. A slice of `days` rather than a second fold, so the two can never disagree
   * about what a Tuesday was, and the SAME constant `recent` folds, so the figure above the strip
   * and the strip under it cannot name different spans.
   *
   * Shorter than a fortnight when the read returned fewer days. Fewer cells is the honest drawing:
   * a padded one would claim the client had a rest day the server said nothing about.
   */
  readonly recentDays: readonly AttendanceDayMark[];
  /** The 30-day window ending today. */
  readonly window: AttendanceWindow;
  /**
   * The last fortnight — the card's hero figure, folded over the span `recentDays` draws. `done` is
   * the DAYS trained (KANON „Karty zadań": „5 z 14 dni z treningiem" — `/consistency` gives one
   * verdict per day), `total - done` the days missed.
   */
  readonly recent: AttendanceWindow;
  /**
   * The newest day the client trained anywhere in the read, or `null` when none of its days was one.
   *
   * The whole window rather than the fortnight: „ostatni trening 19 dni temu" is exactly the
   * sentence a client with an empty fortnight needs, and the fortnight alone cannot say it.
   */
  readonly lastTrained: string | null;
}

/**
 * The card's two spans. Named so a change is one edit rather than a hunt for literals.
 *
 * There used to be three: a seven-day strip sat under a fourteen-day figure. The owner collapsed
 * that on 1 September 2026 and the constant went with it, so the strip cannot drift away from the
 * figure again without somebody deliberately introducing a second number.
 */
export const ATTENDANCE_RECENT_DAYS = 14;
export const ATTENDANCE_WINDOW_DAYS = 30;

function fold(days: readonly AttendanceDay[]): AttendanceWindow {
  let done = 0;
  let total = 0;
  for (const day of days) {
    if (day.state === 'Trained') {
      done += 1;
      total += 1;
    } else if (day.state === 'Missed') {
      total += 1;
    }
  }
  return { done, total, pct: total === 0 ? null : Math.round((done / total) * 100) };
}

/**
 * @param days every day the read returned, oldest first.
 * @param windowDays how many of the most recent days the sentence, the ring and the grid fold.
 *   Defaults to the summary column's thirty. The Trendy tab passes its own range's span, because
 *   the range bar governs every section on that tab and a card that quietly folded thirty days
 *   under a ninety-day range would print a figure the control above it does not explain.
 *
 *   Days outside the window are ignored rather than rejected — a caller that asks the API for more
 *   than it folds gets the same answer as one that asked for exactly the window.
 */
export function buildAttendance(
  days: readonly AttendanceDay[],
  windowDays: number = ATTENDANCE_WINDOW_DAYS,
): AttendanceView {
  // Sorted rather than trusted. The card reads the LAST n entries — an out-of-order response
  // would silently fold the wrong month and put the wrong fortnight in the hero figure.
  const ordered = [...days].sort((left, right) => left.date.localeCompare(right.date));

  const window = ordered.slice(-Math.max(1, windowDays));

  const dayCells = window.map((day, index) => {
    const weekday = mondayFirstWeekday(day.date);
    return {
      date: day.date,
      state:
        day.state === 'Trained'
          ? ('done' as const)
          : day.state === 'Missed'
            ? ('missed' as const)
            : ('free' as const),
      weekday,
      anchor: index === 0 || weekday === 0,
    };
  });

  return {
    days: dayCells,
    // The newest fortnight, not the first: the strip is about the days just gone.
    recentDays: dayCells.slice(-ATTENDANCE_RECENT_DAYS),
    window: fold(window),
    recent: fold(ordered.slice(-ATTENDANCE_RECENT_DAYS)),
    lastTrained: ordered.findLast((day) => day.state === 'Trained')?.date ?? null,
  };
}

/**
 * The line under the fortnight's figure — „2 opuszczone · ostatni trening wczoraj" (KANON „Karty
 * zadań" `.a-att-sub`, 24 September 2026), either half dropped where it has nothing to say.
 *
 * `todayIso` is the page's own clock, the day the `/consistency` window was asked to end on, so the
 * „wczoraj" counts from the same today the strip above it ends at.
 */
export function attendanceSubline(view: AttendanceView, todayIso: string, t: Translate): string {
  const parts: string[] = [];
  const missed = view.recent.total - view.recent.done;
  if (missed > 0) parts.push(t('web:clientDetail.attendance.missed', { count: missed }));
  if (view.lastTrained !== null) {
    const ago = Math.round((parseDateOnly(todayIso) - parseDateOnly(view.lastTrained)) / MS_PER_DAY);
    if (!Number.isNaN(ago)) {
      parts.push(
        t('web:clientDetail.attendance.lastTrained', {
          when:
            ago <= 0
              ? t('web:clientDetail.attendance.lastToday')
              : ago === 1
                ? t('web:clientDetail.attendance.lastYesterday')
                : t('web:clientDetail.attendance.lastDaysAgo', { count: ago }),
        }),
      );
    }
  }
  return parts.join(' · ');
}
